import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  type Modifier
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from '@dnd-kit/sortable'
import { Plus, Settings } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useGlobalUser } from '../../../hooks/useGlobalState'
import { useUserDisplayProfile } from '../../../hooks/useUserDisplayProfile'
import { useLifeSpaces, type LifeSpace, type CreateLifeSpaceData } from '../../../hooks/useLifeSpaces'
import { LifeSpaceButton } from './LifeSpaceButton'
import { UserAvatar } from './UserAvatar'
import { OfflineIndicator } from './OfflineIndicator'
import { CreateLifeSpaceDialog } from './CreateLifeSpaceDialog'
import { EllipsisIcon } from '../../icons/EllipsisIcon'
import { HBaseIcon } from '../../icons/HBaseIcon'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '../../ui/dropdown-menu'
import { TooltipProvider } from '../../ui/tooltip'
import './LifeSpacesSidebar.scss'

/**
 * Props for the {@link LifeSpacesSidebar} component.
 *
 * @property activeSpace - ID of the currently active Life Space
 * @property onSpaceChange - Callback fired when the user switches spaces
 */
export interface LifeSpacesSidebarProps {
  activeSpace: string
  onSpaceChange: (spaceId: string) => void
}

/** 4 default + 10 custom */
const MAX_TOTAL_SPACES = 14

// Memoized LifeSpaceButton wrapper to prevent inline function recreation
const MemoizedLifeSpaceItem = React.memo<{
  space: LifeSpace
  isActive: boolean
  shortcutIndex?: number
  onSpaceChange: (id: string) => void
  onEditSpace: (id: string) => void
  onDeleteSpace: (id: string) => void
}>(({ space, isActive, shortcutIndex, onSpaceChange, onEditSpace, onDeleteSpace }) => {
  const handleClick = useCallback(() => onSpaceChange(space.id), [onSpaceChange, space.id])
  const handleEdit = useCallback(() => onEditSpace(space.id), [onEditSpace, space.id])
  const handleDelete = useCallback(() => onDeleteSpace(space.id), [onDeleteSpace, space.id])

  return (
    <LifeSpaceButton
      space={space}
      isActive={isActive}
      shortcutIndex={shortcutIndex}
      onClick={handleClick}
      onEdit={handleEdit}
      onDelete={handleDelete}
    />
  )
})

const LifeSpacesSidebarComponent: React.FC<LifeSpacesSidebarProps> = ({
  activeSpace,
  onSpaceChange
}) => {
  const { t } = useTranslation()
  const { user } = useGlobalUser()
  const {
    avatarUrl: userAvatarUrl,
    fullName: userFullName,
    isLoading: userProfileLoading,
  } = useUserDisplayProfile(user?.id)
  const { spaces, addSpace, updateSpace, deleteSpace, reorderSpaces } = useLifeSpaces()
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [editingSpace, setEditingSpace] = useState<LifeSpace | null>(null)
  const [moreDropdownOpen, setMoreDropdownOpen] = useState(false)

  const spacesRef = useRef<HTMLDivElement>(null)

  // Memoize sensors to prevent recreation
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  )

  // Restrict drag to stay within the spaces container (vertical only)
  const restrictToSpacesContainer: Modifier = useCallback(({ transform, draggingNodeRect }) => {
    const container = spacesRef.current
    if (!container || !draggingNodeRect) return { ...transform, x: 0 }

    const containerRect = container.getBoundingClientRect()
    const topBound = containerRect.top - draggingNodeRect.top
    const bottomBound = containerRect.bottom - draggingNodeRect.bottom

    return {
      ...transform,
      x: 0,
      y: Math.min(Math.max(transform.y, topBound), bottomBound),
    }
  }, [])

  // Separate "all" from sortable spaces
  const allSpace = useMemo(() => spaces.find(s => s.id === 'all'), [spaces])
  const sortableSpaces = useMemo(() => spaces.filter(s => s.id !== 'all'), [spaces])
  const sortableItems = useMemo(() => sortableSpaces.map(s => s.id), [sortableSpaces])

  // Memoized drag handler to prevent recreation on every render
  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      const oldIndex = spaces.findIndex(s => s.id === active.id)
      const newIndex = spaces.findIndex(s => s.id === over.id)

      if (oldIndex !== -1 && newIndex !== -1) {
        reorderSpaces(oldIndex, newIndex)
      }
    }
  }, [spaces, reorderSpaces])

  // Memoized create/update handler
  const handleCreateSpace = useCallback((data: CreateLifeSpaceData) => {
    try {
      if (editingSpace) {
        updateSpace(editingSpace.id, data)
      } else {
        const newSpace = addSpace(data)
        if (!newSpace) return
      }
      setShowCreateDialog(false)
      setEditingSpace(null)
    } catch (error) {
      console.error('Failed to create/update space:', error)
    }
  }, [editingSpace, updateSpace, addSpace])

  // Memoized edit handler
  const handleEditSpace = useCallback((id: string) => {
    const space = spaces.find(s => s.id === id)
    if (!space) return
    setEditingSpace(space)
    setShowCreateDialog(true)
  }, [spaces])

  // Memoized delete handler
  const handleDeleteSpace = useCallback((id: string) => {
    const space = spaces.find(s => s.id === id)
    if (!space) return

    if (window.confirm(t('lifeSpaces.confirmDelete', { name: space.name }))) {
      try {
        deleteSpace(id)
        if (activeSpace === id) {
          onSpaceChange('personal')
        }
      } catch (error) {
        console.error('Failed to delete space:', error)
      }
    }
  }, [spaces, deleteSpace, activeSpace, onSpaceChange, t])

  // Memoized toggle callbacks
  const handleOpenCreateDialog = useCallback(() => {
    setMoreDropdownOpen(false)
    setShowCreateDialog(true)
  }, [])
  const handleCloseCreateDialog = useCallback(() => {
    setShowCreateDialog(false)
    setEditingSpace(null)
  }, [])

  // Visual order: "All" is always first, then sortable spaces (matches rendered order)
  const visualOrder = useMemo(
    () => (allSpace ? [allSpace, ...sortableSpaces] : sortableSpaces),
    [allSpace, sortableSpaces]
  )

  // Keyboard navigation: Cmd/Ctrl+Option+Arrow for space switching, 1-9 direct, L/Shift+L cycling
  const handleKeyPress = useCallback((e: KeyboardEvent) => {
    // Cmd/Ctrl + Option + Arrow Up/Down — switch Life Space (uses visualOrder for correct ordering)
    if ((e.metaKey || e.ctrlKey) && e.altKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
      e.preventDefault()
      // Claim the event exclusively — list-navigation listeners on window must
      // not also interpret this as Cmd+ArrowUp = jump-to-top.
      e.stopImmediatePropagation()
      const currentIndex = visualOrder.findIndex(s => s.id === activeSpace)
      const len = visualOrder.length
      if (len === 0) return

      if (currentIndex === -1) {
        onSpaceChange(visualOrder[0].id)
        return
      }

      const nextIndex = e.key === 'ArrowUp'
        ? (currentIndex - 1 + len) % len
        : (currentIndex + 1) % len
      onSpaceChange(visualOrder[nextIndex].id)
      return
    }

    // All other shortcuts require no modifiers
    if (e.metaKey || e.ctrlKey || e.altKey) return

    const tag = (e.target as HTMLElement)?.tagName
    if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement)?.isContentEditable) return

    if (e.key >= '1' && e.key <= '9') {
      const index = parseInt(e.key) - 1
      if (visualOrder[index]) {
        e.preventDefault()
        onSpaceChange(visualOrder[index].id)
      }
    }

    // L = next space, Shift+L = previous space (with wrap-around)
    if (e.key === 'l' || e.key === 'L') {
      e.preventDefault()
      const currentIndex = visualOrder.findIndex(s => s.id === activeSpace)
      const len = visualOrder.length
      if (len === 0) return

      if (currentIndex === -1) {
        onSpaceChange(visualOrder[0].id)
        return
      }

      const nextIndex = e.shiftKey
        ? (currentIndex - 1 + len) % len
        : (currentIndex + 1) % len
      onSpaceChange(visualOrder[nextIndex].id)
    }
  }, [visualOrder, activeSpace, onSpaceChange])

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [handleKeyPress])

  return (
    <>
      <TooltipProvider delayDuration={1000}>
      <div className="life-spaces-sidebar">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToSpacesContainer]}
        >
          {/* All button — outside SortableContext, not draggable */}
          {allSpace && (
            <MemoizedLifeSpaceItem
              key={allSpace.id}
              space={allSpace}
              isActive={activeSpace === allSpace.id}
              shortcutIndex={1}
              onSpaceChange={onSpaceChange}
              onEditSpace={handleEditSpace}
              onDeleteSpace={handleDeleteSpace}
            />
          )}

          <SortableContext
            items={sortableItems}
            strategy={verticalListSortingStrategy}
          >
            <div className="life-spaces-sidebar__spaces" ref={spacesRef}>
              {sortableSpaces.map((space, i) => (
                <MemoizedLifeSpaceItem
                  key={space.id}
                  space={space}
                  isActive={activeSpace === space.id}
                  shortcutIndex={i + 2}
                  onSpaceChange={onSpaceChange}
                  onEditSpace={handleEditSpace}
                  onDeleteSpace={handleDeleteSpace}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>

        {/* Shortcut badge */}
        <div className="life-spaces-sidebar__shortcut">L</div>

        {/* More Button */}
        <div className="life-spaces-sidebar__more-wrapper">
          <DropdownMenu open={moreDropdownOpen} onOpenChange={setMoreDropdownOpen}>
            <DropdownMenuTrigger asChild>
              <button
                className="life-spaces-sidebar__more-button"
                title={t('lifeSpaces.more')}
              >
                <span className="life-spaces-sidebar__more-hbase life-spaces-sidebar__more-hbase--hover">
                  <HBaseIcon size={32} color="var(--muted)" />
                </span>
                <span className="life-spaces-sidebar__more-hbase life-spaces-sidebar__more-hbase--pressed">
                  <HBaseIcon size={32} color="var(--border)" />
                </span>
                <span className="life-spaces-sidebar__more-icon">
                  <EllipsisIcon size={16} />
                </span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="right" align="start">
              {spaces.length < MAX_TOTAL_SPACES && (
                <DropdownMenuItem onSelect={handleOpenCreateDialog}>
                  <Plus size={14} className="context-menu-content__item-icon" />
                  {t('lifeSpaces.newLifeSpace')}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onSelect={() => setMoreDropdownOpen(false)}>
                <Settings size={14} className="context-menu-content__item-icon" />
                {t('lifeSpaces.customize')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div
          className={`life-spaces-sidebar__spacer${moreDropdownOpen ? ' life-spaces-sidebar__spacer--no-drag' : ''}`}
          onClick={moreDropdownOpen ? () => setMoreDropdownOpen(false) : undefined}
        />

        <OfflineIndicator />

        <div className="life-spaces-sidebar__footer">
          <UserAvatar
            user={user}
            avatarUrl={userAvatarUrl}
            fullName={userFullName}
            isLoading={userProfileLoading}
          />
        </div>
      </div>
      </TooltipProvider>

      {/* Create/Edit Dialog */}
      {showCreateDialog && (
        <CreateLifeSpaceDialog
          editingSpace={editingSpace}
          onConfirm={handleCreateSpace}
          onCancel={handleCloseCreateDialog}
        />
      )}
    </>
  )
}

/**
 * Narrow left sidebar displaying Life Space icons with drag-and-drop reordering.
 *
 * @description
 * Renders the vertical icon rail on the far left of the app. Contains:
 * - The "All" space button (not draggable)
 * - Sortable default and custom space buttons (via dnd-kit)
 * - A "more" menu for creating new spaces / customization
 * - The user avatar with settings dropdown at the bottom
 *
 * Keyboard shortcut: `1`–`9` switches directly to the corresponding space
 * (ignored when an input field is focused).
 *
 * @example
 * <LifeSpacesSidebar
 *   activeSpace={activeLifeSpace}
 *   onSpaceChange={handleLifeSpaceChange}
 * />
 */
const LifeSpacesSidebar = React.memo(LifeSpacesSidebarComponent)

export default LifeSpacesSidebar
